import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, Box, BoxProps } from '@mui/material';
import { FlagProvider, IConfig } from '@unleash/proxy-client-react';
import { ConfirmProvider } from 'material-ui-confirm';
import { getToken, onMessage, isSupported } from 'firebase/messaging';
import TagManager from 'react-gtm-module';
import { toast, ToastContainer } from 'react-toastify';
import { usePostHog } from 'posthog-js/react';
import 'react-toastify/dist/ReactToastify.css';
import ReactGA from 'react-ga4';

import PushNotification from './components/Notification/PushNotification';
import messaging from './firebase/firebaseConfig';
import { getConfigState, getThemeMode } from './store/ConfigSlice';
import { getAuthState, userSignIn } from './store/AuthSlice';
import { useAppSelector } from './hooks';
import { LightTheme, DarkTheme } from './theme';
import ErrorAlert from './components/Alert/ErrorAlert';
import SuccessAlert from './components/Alert/SuccessAlert';
import FullScreenLoader from './components/Loaders/FullScreenloader';
import FeatureFlagUpdater from './components/FeatureFlag/FeatureFlagUpdater';
import BaseRoute from './BaseRoutes';
import {
  getAuthUser,
  getClientCredentialsTokenV2,
  saveFcmToken,
} from './store/AuthSlice/AuthApi';
import { getTrialsState, toggleBookTrialModal } from './store/V2/TrialsSlice';
import BookTrialModal from './pages/V2/TrialClass/modals/BookTrialModal';
import {
  checkUserPermission,
  getAuthUserFromLocalStorage,
  getFeedbackModalCancelledFromLocalStorage,
} from './helper';
import {
  CHAT_CONNECTION_MODE,
  getChatState,
  initiChatConnection,
  setConnected,
  verifyChatConnection,
} from './store/V2/ChatSlice';
import { getUserFlexiblePlans } from './store/V2/FlexiblePlansSlice';
import { getUserSubscriptionPlans } from './store/V2/SubscriptionPlansSlice';
import { ENV_TYPES } from './constants';
import { adminChatSetConnection } from './store/V2/AdminChatSlice';
import WhatsAppSupportButton from './components/Button/WhatsAppSupportButton';
import { getAppTenant, getTenantsState } from './store/V2/TenantsSlice';
import { USER_PERMISSION_INDIVIDUALS } from './store/V2/RolesSlice';
import { getUsageByUserId } from './store/AccessLimitSlice/AccessLimitApi';
import socket from './helper/Socket';
import { AUTH_TYPES } from './api';
import {
  getLessonStudentFeedbacksState,
  getPendingLessonStudentFeedback,
} from './store/V2/LessonStudentFeedbacksSlice';
import ClassFeedbackModal from './components/V2/Feedback/ClassFeedbackModal';

const containerStyles: BoxProps['sx'] = {
  backgroundColor: 'background.default',
  color: 'text.primary',
};

const TUTOPIYA_GA_TRACKING_ID =
  process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID || '';

const AIBUDDY_GA_TRACKING_ID =
  process.env.REACT_APP_AIBUDDY_GOOGLE_ANALYTICS_TRACKING_ID || '';

const featureFlagConfig: IConfig = {
  url: `${process.env.REACT_APP_FEATURE_FLAG_URL}/proxy`, // Your front-end API URL or the Unleash proxy's URL (https://<proxy-url>/proxy)
  clientKey: process.env.REACT_APP_FEATURE_FLAG_CLIENT_KEY as string, // A client-side API token OR one of your proxy's designated client keys (previously known as proxy secrets)
  refreshInterval: 0, // How often (in seconds) the client should poll the proxy for updates
  appName: process.env.REACT_APP_FEATURE_FLAG_APP_NAME as string, // The name of your application. It's only used for identifying your application
};

const App: React.FC = () => {
  const dispatch = useDispatch();
  const posthog = usePostHog();

  // Store
  const configState = useAppSelector(getConfigState);
  const themeMode = useAppSelector(getThemeMode);
  const authState = useAppSelector(getAuthState);
  const trialsState = useAppSelector(getTrialsState);
  const chatState = useAppSelector(getChatState);
  const tenantState = useAppSelector(getTenantsState);
  const lessonStudentFeedbacksState = useAppSelector(
    getLessonStudentFeedbacksState
  );

  const { user: authUser } = authState;
  const { adminEditMode } = configState;
  const { appTenant } = tenantState;
  const { selectedConversation: userChatSelectedConversation } = chatState;
  const { pendingLessonStudentFeedback } = lessonStudentFeedbacksState;

  // State
  const [initialLoading, setInitiaLoading] = useState(true);
  const [isInitiated, setInitiated] = useState(false);
  const [showClassFeedbackModal, setShowClassFeedbackModal] = useState(false);

  const urlArr = window.location.href.split('#');
  const newUrl = new URL(`${urlArr[0]}?${window.location.hash.substr(1)}`);
  const TPAuthToken = newUrl.searchParams.get('id_token') || undefined;
  const TPAuthLoginUserId = newUrl.searchParams.get('user_id') || undefined;
  const theme = themeMode === 'light' ? LightTheme : DarkTheme;

  // Permissions
  const hasUsagePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      resources: [USER_PERMISSION_INDIVIDUALS.RESOURCES_USAGE],
    },
  });

  const isNotificationAllowed = (payload) => {
    if (payload?.notification?.title) {
      const _titleArr = payload.notification.title.split(' ');
      // DISABLE CHAT NOTIFICATION
      if (
        _titleArr.length > 1 &&
        _titleArr[0] === 'New' &&
        _titleArr[1] === 'Message' &&
        payload.data?.courseId === userChatSelectedConversation?.course
      ) {
        return false;
      }
    }
    return true;
  };

  onMessage(messaging, (payload) => {
    const isAllowed = isNotificationAllowed(payload);

    if (isAllowed) {
      toast(<PushNotification notification={payload.notification} />);
    }
  });

  useEffect(() => {
    initiate();
  }, []);

  useEffect(() => {
    initAuthUser().then();
  }, [authState.accessToken]);

  useEffect(() => {
    if (!isInitiated && authUser && appTenant && !authUser.role.isGuest) {
      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            course: [USER_PERMISSION_INDIVIDUALS.COURSE_CHAT],
            adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CHAT],
            class: [USER_PERMISSION_INDIVIDUALS.CLASS_CHAT],
            adminClass: [USER_PERMISSION_INDIVIDUALS.ADMIN_CLASS_CHAT],
          },
        })
      ) {
        dispatch(initiChatConnection());
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            billing: [USER_PERMISSION_INDIVIDUALS.BILLING_VIEW],
          },
        })
      ) {
        dispatch(getUserFlexiblePlans());
        dispatch(getUserSubscriptionPlans());
      }

      // PostHog identify user
      posthog.identify(authUser._id, {
        name: `${authUser.firstname} ${authUser.lastname}`,
        email: authUser.email,
        role: authUser.role.name,
        tenant: appTenant?.key,
        firstname: authUser.firstname,
        lastname: authUser.lastname,
      });

      dispatch(getPendingLessonStudentFeedback());

      setInitiated(true);
    }
  }, [authUser, appTenant]);

  useEffect(() => {
    if (authUser && hasUsagePermission) {
      dispatch(getUsageByUserId(authUser._id));
    }
  }, [authUser?._id, hasUsagePermission]);

  useEffect(() => {
    if (chatState.initiated) {
      dispatch(
        verifyChatConnection({
          isAdminModeEnabled: adminEditMode,
        })
      );
    }
  }, [chatState.initiated]);

  useEffect(() => {
    if (chatState.chatConnectionMode === CHAT_CONNECTION_MODE.ADMIN) {
      dispatch(adminChatSetConnection(true));
    } else if (chatState.chatConnectionMode === CHAT_CONNECTION_MODE.USER) {
      dispatch(setConnected(true));
    } else {
      dispatch(adminChatSetConnection(false));
      dispatch(setConnected(false));
    }
  }, [chatState.chatConnectionMode]);

  useEffect(() => {
    if (pendingLessonStudentFeedback?._id) {
      const feedbackModalCancelled = getFeedbackModalCancelledFromLocalStorage();
      if (feedbackModalCancelled !== pendingLessonStudentFeedback._id) {
        setShowClassFeedbackModal(true);
      }
    } else {
      setShowClassFeedbackModal(false);
    }
  }, [pendingLessonStudentFeedback]);

  const initiate = async () => {
    const { hostname } = window.location;
    // ReactGA.pageview(window.location.pathname + window.location.search);

    if (hostname === 'www.tutopiya.com') {
      TagManager.initialize({ gtmId: 'GTM-WCC337Q' });
      ReactGA.initialize(TUTOPIYA_GA_TRACKING_ID);
    } else if (hostname === 'www.aibuddy.sg') {
      TagManager.initialize({ gtmId: 'GTM-MQFBQSXT' });
      ReactGA.initialize(AIBUDDY_GA_TRACKING_ID);
    }

    await dispatch(getAppTenant());

    if (TPAuthToken && TPAuthLoginUserId) {
      await dispatch(
        userSignIn({
          auth_type: AUTH_TYPES.TP_TOKEN,
          auth_token: TPAuthToken,
          user_id: TPAuthLoginUserId,
        })
      );
    } else if (!authState.accessToken) {
      await dispatch(getClientCredentialsTokenV2());
    }
  };

  async function requestPermission() {
    const isSupportedBrowser = await isSupported();

    if (isSupportedBrowser) {
      try {
        // Unregister existing service workers to avoid conflicts
        const existingRegistrations = await navigator.serviceWorker.getRegistrations();
        console.log('Existing registrations:', existingRegistrations.length);

        // eslint-disable-next-line no-restricted-syntax
        for (const registration of existingRegistrations) {
          // eslint-disable-next-line no-await-in-loop
          await registration.unregister();
          console.log('Unregistered existing service worker');
        }
        // Register the service worker
        const serviceWorkerRegistration = await navigator.serviceWorker.register(
          `${process.env.PUBLIC_URL}/firebase-messaging-sw.js`
        );

        // Wait for the service worker to activate with a timeout
        const waitForActivation = new Promise<ServiceWorkerRegistration>(
          (resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Service worker activation timeout'));
            }, 10000); // 10 second timeout

            if (serviceWorkerRegistration.active) {
              clearTimeout(timeout);
              resolve(serviceWorkerRegistration);
            } else if (serviceWorkerRegistration.installing) {
              serviceWorkerRegistration.installing.onstatechange = (event) => {
                const sw = event.target as ServiceWorker;
                if (sw.state === 'activated') {
                  clearTimeout(timeout);
                  resolve(serviceWorkerRegistration);
                }
              };
            } else {
              // Wait for ready
              navigator.serviceWorker.ready.then((reg) => {
                clearTimeout(timeout);
                resolve(reg);
              });
            }
          }
        );

        await waitForActivation;

        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
          const token = await getToken(messaging, {
            serviceWorkerRegistration,
          });

          dispatch(saveFcmToken({ token }));
        } else if (permission === 'denied') {
          // notifications are blocked
          // eslint-disable-next-line no-console
          console.log('You denied for the notification');
        }
      } catch (error) {
        console.log('Error in requestPermission:', error);
      }
    }
  }

  const initAuthUser = async () => {
    if (authState.accessToken && authState.user) {
      await dispatch(getAuthUser());

      const localStorageAuthUser = getAuthUserFromLocalStorage();

      if (localStorageAuthUser && localStorageAuthUser._id) {
        await socket.connect();
      }

      if (
        authState.user &&
        !authState.user.role.isGuest &&
        process.env.REACT_APP_ENV !== ENV_TYPES.LOCAL
      ) {
        await requestPermission().then();
      }

      setInitiaLoading(false);
    } else if (authState.accessToken) {
      setInitiaLoading(false);
    }
  };

  if (initialLoading || (tenantState.status === 'loading' && !appTenant)) {
    return <FullScreenLoader />;
  }

  return (
    <FlagProvider config={featureFlagConfig}>
      <FeatureFlagUpdater />
      <ThemeProvider theme={theme}>
        <ConfirmProvider>
          <Box
            data-testid="main-app-container"
            className="App"
            sx={containerStyles}
          >
            <BrowserRouter>
              <Routes>
                <Route
                  path={`${process.env.PUBLIC_URL}/*`}
                  element={<BaseRoute />}
                />
              </Routes>
            </BrowserRouter>
            <ErrorAlert />
            <SuccessAlert />
            {/* MODALS */}
            {trialsState.bookTrialModalOpen && (
              <BookTrialModal
                open={trialsState.bookTrialModalOpen}
                onClose={() => dispatch(toggleBookTrialModal())}
              />
            )}
            {pendingLessonStudentFeedback && (
              <ClassFeedbackModal
                open={showClassFeedbackModal}
                onClose={() => setShowClassFeedbackModal(false)}
                classData={pendingLessonStudentFeedback}
              />
            )}
          </Box>
        </ConfirmProvider>
      </ThemeProvider>
      <ToastContainer />
      <WhatsAppSupportButton />
    </FlagProvider>
  );
};

export default App;
